import React, { useState, useEffect } from 'react';
import { ArrowLeft, Upload, Plus, X, Clock } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import ImageUpload from '@/components/ImageUpload';
import { useImageStorage } from '@/hooks/useImageStorage';

interface ImageFile {
  id: string;
  file: File;
  preview: string;
  name: string;
  size: number;
}

// Mock data for editing - in real app this would come from API/database
const mockProductData = {
  301: {
    id: 301,
    name: "Social Media Mastery E-book",
    description: "Complete gids voor social media marketing met praktische tips en templates.",
    price: "35",
    category: "Digitale Producten",
    type: "digital",
    stock: "",
    tags: ["E-book", "Social Media", "Marketing"],
    images: [] as ImageFile[]
  },
  302: {
    id: 302,
    name: "Instagram Template Pack",
    description: "30 professionele Instagram post templates voor ondernemers.",
    price: "25",
    category: "Digitale Producten", 
    type: "digital",
    stock: "",
    tags: ["Templates", "Instagram", "Design"],
    images: [] as ImageFile[]
  },
  303: {
    id: 303,
    name: "Business Goal Planner",
    description: "Fysieke planner voor het stellen en behalen van business doelen.",
    price: "45",
    category: "Business Tools",
    type: "physical",
    stock: "25",
    tags: ["Planner", "Goals", "Business"],
    images: [] as ImageFile[]
  }
};
const productCategories = [
  'Digitale Producten',
  'Creative Producten',
  'Business Tools',
  'Lifestyle Producten',
  'Beauty & Wellness',
  'Fashion & Accessoires',
];
export const AddProductForm = ({
  formData,
  setFormData,
  isLoading,
  handleInputChange,
  handleImagesChange,
  handleSubmit,
  navigate,
  isEditMode,
  newTag,
  setNewTag,
  addTag,
  removeTag,
  productCategories
}) => (
  <div className="min-h-screen pb-32" style={{ backgroundColor: '#f6f1eb' }}>
    {/* Header */}
    <div className="bg-white shadow-sm" style={{ borderBottom: '1px solid #efe7dd' }}>
      <div className="max-w-2xl mx-auto px-6 py-4">
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              if (window.history.length > 2) {
                navigate(-1)
              } else {
                navigate('/')
              }
            }}
            className="p-2 hover:bg-white/50"
            style={{ color: '#837069' }}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold" style={{ color: '#837069' }}>
              {isEditMode ? 'Product Bewerken' : 'Product Toevoegen'}
            </h1>
            <p className="text-sm" style={{ color: '#b6a092' }}>
              {isEditMode 
                ? 'Pas je product aan en sla de wijzigingen op' 
                : 'Deel je product met de WOM community'
              }
            </p>
          </div>
        </div>

        {/* Product Header */}
        <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: '#efe7dd', border: '1px solid #e6dbd0' }}>
          <div className="text-center">
            <div className="mb-2">
              <h1 className="text-2xl font-light tracking-wider" style={{ color: '#837069' }}>
                <span className="font-bold" style={{ color: '#a0897c' }}>
                  {isEditMode ? 'Product Bewerken' : 'Nieuw Product'}
                </span>
              </h1>
            </div>
            <p className="text-sm" style={{ color: '#b6a092' }}>
              {isEditMode 
                ? 'Verfijn je product voor de WOM community' 
                : 'Deel je creaties met vrouwen die jouw producten waarderen'
              }
            </p>
          </div>
        </div>
        {/* Edit Mode Badge */}
        {isEditMode && (
          <div className="mt-3">
            <Badge variant="outline" className="text-xs" style={{ backgroundColor: '#fbf8f4', color: '#a0897c', borderColor: '#efe7dd' }}>
              <Clock className="h-3 w-3 mr-1" />
              Bewerken: {formData.name || 'Product'}
            </Badge>
          </div>
        )}
      </div>
    </div>

    {/* Form */}
    <div className="max-w-2xl mx-auto px-6 py-8 pb-16">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info Card */}
        <Card style={{ border: '1px solid #efe7dd' }}>
          <CardHeader>
            <CardTitle className="text-lg" style={{ color: '#837069' }}>Basis Informatie</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name" style={{ color: '#837069' }}>Product Naam *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Bijv. Social Media Templates Pack"
                className="mt-1"
                style={{ borderColor: '#efe7dd' }}
              />
            </div>

            <div>
              <Label htmlFor="description" style={{ color: '#837069' }}>Beschrijving *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Beschrijf je product, wat maakt het bijzonder?"
                className="mt-1 h-24"
                style={{ borderColor: '#efe7dd' }}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price" style={{ color: '#837069' }}>Prijs (€) *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  placeholder="25.00"
                  className="mt-1"
                  style={{ borderColor: '#efe7dd' }}
                />
              </div>
              <div>
                <Label htmlFor="stock" style={{ color: '#837069' }}>Voorraad</Label>
                <Input
                  id="stock"
                  type="number"
                  value={formData.stock}
                  onChange={(e) => handleInputChange('stock', e.target.value)}
                  placeholder="Onbeperkt laat leeg"
                  className="mt-1"
                  style={{ borderColor: '#efe7dd' }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Category & Type Card */}
        <Card style={{ border: '1px solid #efe7dd' }}>
          <CardHeader>
            <CardTitle className="text-lg" style={{ color: '#837069' }}>Categorie & Type</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label style={{ color: '#837069' }}>Categorie *</Label>
              <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                <SelectTrigger className="mt-1" style={{ borderColor: '#d9cbbf', backgroundColor: '#efe7dd' }}>
                  <SelectValue placeholder="Kies een categorie" />
                </SelectTrigger>
                <SelectContent style={{ backgroundColor: '#efe7dd', border: '1px solid #d9cbbf' }} className="z-[100]">
                  {(productCategories ?? []).map((category) => (
                    <SelectItem key={category} value={category} style={{ backgroundColor: '#efe7dd' }}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label style={{ color: '#837069' }}>Product Type</Label>
              <Select value={formData.type} onValueChange={(value) => handleInputChange('type', value)}>
                <SelectTrigger className="mt-1" style={{ borderColor: '#d9cbbf', backgroundColor: '#efe7dd' }}>
                  <SelectValue placeholder="Kies product type" />
                </SelectTrigger>
                <SelectContent style={{ backgroundColor: '#efe7dd', border: '1px solid #d9cbbf' }} className="z-[100]">
                  <SelectItem value="digital" style={{ backgroundColor: '#efe7dd' }}>Digitaal Product</SelectItem>
                  <SelectItem value="physical" style={{ backgroundColor: '#efe7dd' }}>Fysiek Product</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tags Card */}
        <Card style={{ border: '1px solid #efe7dd' }}>
          <CardHeader>
            <CardTitle className="text-lg" style={{ color: '#837069' }}>Tags</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder="Voeg een tag toe"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                style={{ borderColor: '#efe7dd' }}
              />
              <Button 
                type="button" 
                onClick={addTag} 
                size="sm"
                style={{ backgroundColor: '#a0897c', color: 'white' }}
                className="hover:opacity-90"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {(formData.tags ?? []).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {(formData.tags ?? []).map((tag) => (
                  <Badge key={tag} variant="secondary" className="flex items-center gap-1" style={{ backgroundColor: '#efe7dd', color: '#837069' }}>
                    {tag}
                    <X 
                      className="h-3 w-3 cursor-pointer" 
                      onClick={() => removeTag(tag)}
                    />
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Images Card */}
        <Card style={{ border: '1px solid #efe7dd' }}>
          <CardHeader>
            <CardTitle className="text-lg" style={{ color: '#837069' }}>Foto's</CardTitle>
            <p className="text-sm" style={{ color: '#b6a092' }}>
              Laat je product zien - goede foto's verkopen beter!
            </p>
          </CardHeader>
          <CardContent>
            <ImageUpload
              images={formData.images}
              onImagesChange={handleImagesChange}
              maxImages={5}
              maxSizeInMB={5}
            />
          </CardContent>
        </Card>

        {/* Submit Buttons */}
        <div className="flex gap-4 pt-6 pb-8">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => {
              if (window.history.length > 2) {
                navigate(-1)
              } else {
                navigate('/')
              }
            }}
            className="flex-1 hover:bg-white/50"
            style={{ borderColor: '#efe7dd', color: '#837069' }}
          >
            Annuleren
          </Button>
          <Button 
            type="submit" 
            className="flex-1 hover:opacity-90"
            style={{ backgroundColor: '#a0897c', color: 'white' }}
          >
            {isEditMode ? 'Wijzigingen Opslaan' : 'Product Publiceren'}
          </Button>
        </div>
      </form>
    </div>
  </div>
);

const AddProduct = ({ initialData }) => {
  const navigate = useNavigate();
  const { productId } = useParams(); // For edit mode: /add-product/edit/:productId
  const isEditMode = Boolean(productId);
  const { images: storedImages, updateImages, clearImages } = useImageStorage(
    isEditMode ? `product-images-edit-${productId}` : 'product-images'
  );
  const [formData, setFormData] = useState(initialData || {
    name: '',
    description: '',
    price: '',
    category: '',
    type: '',
    stock: '',
    tags: [] as string[],
    images: storedImages
  });

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);
  const [newTag, setNewTag] = useState('');
  const [isLoading, setIsLoading] = useState(isEditMode);

  // Load existing product data in edit mode
  useEffect(() => {
    if (isEditMode && productId) {
      setIsLoading(true);
      // Simulate API call - in real app this would be an actual API call
      setTimeout(() => {
        const existingProduct = mockProductData[productId as keyof typeof mockProductData];
        if (existingProduct) {
          setFormData({
            name: existingProduct.name,
            description: existingProduct.description,
            price: existingProduct.price,
            category: existingProduct.category,
            type: existingProduct.type,
            stock: existingProduct.stock,
            tags: existingProduct.tags,
            images: storedImages // Use stored images or existing images
          });
          console.log('Loaded product data for editing:', existingProduct);
          toast.info('Product data geladen voor bewerking');
        } else {
          toast.error('Product niet gevonden');
          navigate('/marketplace');
        }
        setIsLoading(false);
      }, 500); // Simulate network delay
    }
  }, [isEditMode, productId, navigate, storedImages]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !(formData.tags ?? []).includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags ?? []), newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: (prev.tags ?? []).filter(tag => tag !== tagToRemove)
    }));
  };

  const handleImagesChange = (images: ImageFile[]) => {
    setFormData(prev => ({
      ...prev,
      images
    }));
    updateImages(images);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Basic validation
    if (!formData.name || !formData.description || !formData.price || !formData.category) {
      toast.error('Vul alle verplichte velden in');
      return;
    }

    // Filter images safely before accessing file properties
    const validImages = (formData.images ?? []).filter(img => img && typeof img === 'object' && 'file' in img && img.file);
    console.log(`${isEditMode ? 'Updating' : 'Creating'} product:`, {
      ...formData,
      imageCount: validImages.length,
      imageNames: validImages.map(img => img.name),
      productId: isEditMode ? productId : 'new'
    });

    if (isEditMode) {
      toast.success(`Product "${formData.name}" succesvol bijgewerkt!`);
    } else {
      if (validImages.length > 0) {
        toast.success(`Product succesvol toegevoegd met ${validImages.length} foto${validImages.length === 1 ? '' : 's'}!`);
      } else {
        toast.success('Product succesvol toegevoegd!');
      }
    }

    // Clear stored images after successful submission
    clearImages();
    // Navigate back to marketplace or product detail
    if (isEditMode) {
      navigate(`/product/${productId}`);
    } else {
      navigate('/marketplace');
    }
  };

  // Update form when stored images change
  useEffect(() => {
    setFormData(prev => ({ ...prev, images: storedImages }));
  }, [storedImages]);

  // Show loading state in edit mode
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f6f1eb' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" style={{ borderColor: '#a0897c' }}></div>
          <p style={{ color: '#b6a092' }}>Product data laden...</p>
        </div>
      </div>
    );
  }

  return (
    <AddProductForm
      formData={formData}
      setFormData={setFormData}
      isLoading={isLoading}
      handleInputChange={handleInputChange}
      handleImagesChange={handleImagesChange}
      handleSubmit={handleSubmit}
      navigate={navigate}
      isEditMode={isEditMode}
      newTag={newTag}
      setNewTag={setNewTag}
      addTag={addTag}
      removeTag={removeTag}
      productCategories={productCategories}
    />
  );
};

export default AddProduct;